import React, { useCallback, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  Typography,
  LinearProgress,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import {
  AUTH,
  BULK_UPLOAD_COLUMN_COUNT_ERROR,
  BULK_UPLOAD_FILE_SIZE,
  POST_FILE_ACCESS,
  SOMS_API_KEY,
  SOS_BASE_URL,
} from "services/CONSTANTS";
import { showErrorMessageByCode } from "services/commonServices";
import { showNotificationDialog } from "redux/actions/notificationActions";
import { useDispatch } from "react-redux";
import { showNotificationDialogEmergencyOrder } from "redux/actions/notificationEmergencyOrderActions";
import BulkUploadMarqueeComponent from "components/common/BulkUploadMarquee";

const baseStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "20px",
  borderWidth: 2,
  borderRadius: 2,
  borderColor: "#eeeeee",
  borderStyle: "dashed",
  backgroundColor: "#fafafa",
  color: "#bdbdbd",
  outline: "none",
  transition: "border .24s ease-in-out",
};

const focusedStyle = {
  borderColor: "#2196f3",
};

const acceptStyle = {
  borderColor: "#00e676",
};

const rejectStyle = {
  borderColor: "#ff1744",
};

export const BulkUpload = () => {
  const [acceptedFiles, setAcceptedFiles] = useState([]);
  const [fileError, setFileError] = useState(null);
  const [showFileUploadPopup, setShowFileUploadPopup] = useState(false);

  const dispatch = useDispatch();

  const onDrop = useCallback(
    (files, fileRejections) => {
      if (!files.length) {
        const showErrorMessage = fileRejections
          .flatMap((x) => x.errors)
          .map((x) => x.message)
          .join(",");
        if (showErrorMessage === "File type must be text/csv,.csv") {
          let str = showErrorMessage.replace(
            showErrorMessage,
            "Incorrect file format, please upload CSV file."
          );
          setFileError(str);
          dispatch(
            showNotificationDialogEmergencyOrder("Upload Failed", str, "Done")
          );
        } else {
          setFileError(null);
        }
      } else {
        setFileError(null);
      }

      setAcceptedFiles([...acceptedFiles, ...files]);
      setShowFileUploadPopup(false);
    },
    [acceptedFiles, dispatch]
  );

  const {
    fileRejections,
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
    },
    multiple: false,
    disabled: acceptedFiles.length,
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  const removeFile = (file) => () => {
    const files = [...acceptedFiles];
    files.splice(files.indexOf(file), 1);
    setAcceptedFiles(files);
  };

  const showAcceptedFiles = acceptedFiles.map((file) => (
    <ListItem key={file.path} sx={{ pl: 0 }} dense={true} disablePadding={true}>
      <ListItemIcon sx={{ minWidth: "auto", pr: 1 }}>
        <DescriptionOutlinedIcon />
      </ListItemIcon>
      {file.path}
      <Button component={Link} onClick={removeFile(file)} color="error">
        Remove
      </Button>
    </ListItem>
  ));

  const fileRejectionItems = fileRejections.map(({ file }) => (
    <>
      <ListItem
        key={file.path}
        sx={{ pl: 0 }}
        dense={true}
        disablePadding={true}
      >
        <ListItemIcon sx={{ minWidth: "auto", pr: 1, color: "red" }}>
          <DescriptionOutlinedIcon />
        </ListItemIcon>
        {file.path}
      </ListItem>
    </>
  ));

  const handleUploadFile = async (e) => {
    e.preventDefault();

    if (!acceptedFiles.length) {
      dispatch(
        showNotificationDialogEmergencyOrder(
          "No file selected",
          "Please select a file",
          "Done"
        )
      );
      return;
    }

    const file = acceptedFiles[0];

    const maxFileSizeMB = 50;
    const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;

    if (file.size > maxFileSizeBytes) {
      dispatch(
        showNotificationDialogEmergencyOrder(
          "Upload Failed",
          BULK_UPLOAD_FILE_SIZE,
          "Done"
        )
      );
      return;
    }


    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter(Boolean);

      const firstLineColumns = lines[0].split(",").map((col) => col.trim());

      if (firstLineColumns.length !== 16) {
        dispatch(
          showNotificationDialogEmergencyOrder(
            "Upload Failed",
            BULK_UPLOAD_COLUMN_COUNT_ERROR,
            "Done"
          )
        );
        return;
      } else {
        setShowFileUploadPopup(true);

        //Generate new file name
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
        const randomNum = crypto.getRandomValues(new Uint32Array(1))[0];
        const fileName = `emergency_bulk_order_${randomNum}_${timestamp}.csv`;

        //Call backend to get presigned URL
        const presignResponse = await fetch(
          `${SOS_BASE_URL}${POST_FILE_ACCESS}?apikey=${SOMS_API_KEY}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json;charset=UTF-8",
              Authorization: "Basic " + window.atob(window.btoa(AUTH)),
            },
            body: JSON.stringify({
              fileName: fileName,
              reportType: "emergencyBulkOrders",
            }),
          }
        );

        if (!presignResponse.ok) {
          throw new Error("Failed to fetch presigned URL");
        }
        const { fileURL } = await presignResponse.json();

        //Upload file directly to S3
        const uploadResponse = await fetch(fileURL, {
          method: "PUT",
          headers: {
            "Content-Type": "application/octet-stream",
          },
          body: file,
        });

        if (uploadResponse.ok) {
          dispatch(
            showNotificationDialogEmergencyOrder(
              "Submitted Successfully",
              "Emergency bulk orders file submitted successfully",
              "Done"
            )
          );
        } else {
          throw new Error("Upload to S3 failed");
        }

        setShowFileUploadPopup(false);
      }
    } catch (err) {
      console.error(err);
      setShowFileUploadPopup(false);
      const { errTitle, errContent } = showErrorMessageByCode(err);
      dispatch(showNotificationDialog(errTitle, errContent, "Done"));
      setShowFileUploadPopup(false);
    }
  };

  return (
    <>
      {showFileUploadPopup ? (
        <Dialog open={true} maxWidth="sm" fullWidth={true}>
          <DialogTitle>File Uploading</DialogTitle>
          <DialogContent>
            <LinearProgress />
            <Typography>
              To ensure the successful upload, please avoid clicking the back
              button, leaving the page, or refreshing it during the upload
              process
            </Typography>
          </DialogContent>
        </Dialog>
      ) : (
        <div></div>
      )}
      <BulkUploadMarqueeComponent />
      <Grid
        container
        rowSpacing={2}
        columnSpacing={1}
        component="form"
        onSubmit={handleUploadFile}
        sx={{ "& .MuiFormHelperText-root": { ml: 0 } }}
      >
        <Grid container spacing={1} item>
          <Grid item xs={12}>
            <div {...getRootProps({ style })} className="uploadPageStyling">
              <input data-testid="dropzone" {...getInputProps()} />
              <FileUploadOutlinedIcon fontSize="large" />
              <span>Drag and drop to upload or browse for files</span>
              <Typography variant="body2">(Only csv file with size &lt;= 50 MB is allowed)</Typography>
              {isDragReject && (
                <Typography color="error.main">
                  Some file types are not supported
                </Typography>
              )}
            </div>
            <Typography variant="caption" color="error.main">
              {!showAcceptedFiles.length ? fileError : ""}
            </Typography>
            {showAcceptedFiles && (
              <>
                <aside className="asideStyle">
                  <List sx={{ pt: 0.5 }} dense={true}>
                    {showAcceptedFiles} {fileRejectionItems}
                  </List>
                </aside>
              </>
            )}
          </Grid>
        </Grid>
        <div className="commonButtons">
          <Grid container item sx={{ textAlign: "left" }}>
            <Grid item xs={4} sm={2}></Grid>
            <Grid item xs={4} sm={2}>
              {acceptedFiles[0]?.path ? (
                <Button variant="contained" type="submit">
                  Upload
                </Button>
              ) : (
                <Button variant="contained" type="submit" disabled>
                  Upload
                </Button>
              )}
              <Grid sx={{ paddingBottom: 17 }}></Grid>
            </Grid>
          </Grid>
        </div>
      </Grid>
      <Grid></Grid>
    </>
  );
};
